import type { ActionIconProps, PaperProps } from '@mantine/core'
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Highlight,
  Indicator,
  LoadingOverlay,
  Paper,
  Select,
  Skeleton,
  // eslint-disable-next-line no-restricted-imports -- this is the customized Table, built on Mantine's primitives
  Table as MantineTable,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useDebouncedCallback, useMediaQuery } from '@mantine/hooks'
import type { IconProps } from '@tabler/icons-react'
import {
  IconArchive,
  IconArrowsSort,
  IconChevronLeft,
  IconChevronLeftPipe,
  IconChevronRight,
  IconChevronRightPipe,
  IconCircleX,
  IconDotsVertical,
  IconEye,
  IconFilter,
  IconFilterOff,
  IconGripHorizontal,
  IconHelp,
  IconLock,
  IconPencil,
  IconPlayerPlay,
  IconSearch,
  IconSettings,
  IconSortAscending,
  IconSortDescending,
  IconTrash,
  IconWorldShare,
  IconX,
  IconZoomCancel,
} from '@tabler/icons-react'
import type {
  Cell,
  Column,
  ColumnFiltersState,
  Header,
  OnChangeFn,
  PaginationState,
  RowData,
  SortingState,
  Updater,
} from '@tanstack/react-table'
import { functionalUpdate, useTable } from '@tanstack/react-table'
import type { VirtualItem, Virtualizer } from '@tanstack/react-virtual'
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual'
import type { ComponentPropsWithRef, ComponentType, CSSProperties, DragEvent, ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { EmptyStateProps } from '@/components/EmptyState'
import { EmptyState } from '@/components/EmptyState'
import { theme } from '@/configs/theme'
import { useAutoScrollOnDrag } from '@/hooks/useAutoScrollOnDrag'
import * as classes from '@/styles/Table.css'
import type { MarkOptional, TypeOrTypeFn } from '@/types/helpers'
import type { AppTableFeatures, TableColumnDef, TableRow } from '@/utils/table'
import { appTableFeatures, rankByGlobalMatch } from '@/utils/table'

const rowActionsIconRegistry = {
  edit: IconPencil,
  view: IconEye,
  delete: IconTrash,
  archive: IconArchive,
  demo: IconPlayerPlay,
  menu: IconDotsVertical,
  changeStatus: IconWorldShare,
  owners: IconSettings,
  lock: IconLock,
}

export type RowAction = keyof typeof rowActionsIconRegistry

const rowActionsIconStrokeMap: Record<RowAction, number> = {
  edit: 2,
  view: 1.5,
  delete: 1.5,
  archive: 1.5,
  demo: 1.5,
  menu: 2,
  changeStatus: 2,
  owners: 1.5,
  lock: 1.5,
}

const rowActionsIconColorMap: Record<RowAction, string> = {
  edit: 'indigo',
  view: 'blue',
  delete: 'red.7',
  archive: 'orange.7',
  demo: 'blue',
  menu: 'gray',
  changeStatus: 'gray',
  owners: 'blue',
  lock: 'gray',
}

const ACTIONS_COLUMN_ID = 'row-actions'
const DRAG_COLUMN_ID = 'row-drag'

const defaultColumn = {
  size: 180,
  minSize: 40,
  maxSize: 1000,
  cell: ({ cell }: { cell: Cell<AppTableFeatures, any, unknown> }) => <TableCellValue cell={cell} />,
}

const emptySorting: SortingState = []
const noColumnFilters: ColumnFiltersState = []
const noColumnVisibility: Record<string, boolean> = {}

type TablePagination = {
  state: PaginationState
  /** Total rows on the server; the table only ever holds the current page. */
  rowCount: number
  onChange: OnChangeFn<PaginationState>
  showRowsPerPage?: boolean
}

export interface TableProps<DataT extends RowData> {
  data: DataT[]
  columns: TableColumnDef<DataT>[]
  getRowId?: (row: DataT, index: number) => string
  entity?: { singular: string; plural: string; Icon?: ComponentType<IconProps> }
  isLoading?: boolean
  EmptyStateProps?: TableEmptyRowsFallbackProps['EmptyStateProps']

  enableTopToolbar?: boolean
  enableTableHead?: boolean
  toolbarActionsInclude?: TableTopToolbarProps['include']
  toolbarActionsLabelMap?: Partial<Record<TableToolbarAction, string>>
  toolbarActionsDisabled?: Partial<Record<TableToolbarAction, boolean>>
  toolbarActionsDisabledTooltip?: Partial<Record<TableToolbarAction, string>>
  onToolbarActionClick?: TableTopToolbarProps['onActionClick']
  /** Extra custom buttons rendered in the top toolbar, before the built-in actions. */
  extraToolbarActions?: ReactNode
  /** Accessible name for the search box. Defaults to `Search {entity.plural}`. */
  searchLabel?: string

  enableGlobalFilter?: boolean
  /** Controls the search term. Omit it and the table keeps its own. */
  globalFilter?: string
  onGlobalFilterChange?: (value: string) => void
  /** The search term filters on the server, so the table does not filter the rows it is given. */
  manualFiltering?: boolean

  enableSorting?: boolean
  initialSorting?: SortingState
  /** Controls sorting, which the server then applies. */
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  /** Shift-click adds a column to the sort. Turn it off when the server sorts by one column only. */
  enableMultiSort?: boolean
  /**
   * Values for the columns with a `meta.filter`, which show in a filter row the toolbar toggles.
   * Controlled only, and meant for `manualFiltering`, where the owner applies them on the server.
   * Without `onColumnFiltersChange` the table shows no filters.
   */
  columnFilters?: ColumnFiltersState
  onColumnFiltersChange?: (columnFilters: ColumnFiltersState) => void
  columnVisibility?: Record<string, boolean>
  /** Pages on the server. The table shows the pager and leaves the rows it is given in place. */
  pagination?: TablePagination

  actionsColumnPosition?: 'first' | 'last'
  actionsColumnSize?: number
  rowActionsInclude?: TableRowActionsProps<DataT>['include']
  rowActionsTooltipLabels?: TableRowActionsProps<DataT>['tooltipLabels']
  rowActionsButtonProps?: TableRowActionsProps<DataT>['actionButtonProps']
  onRowActionClick?: TableRowActionsProps<DataT>['onActionClick']
  renderRowActions?: (props: { row: TableRow<DataT> }) => ReactNode

  enableRowOrdering?: boolean
  onRowOrderChange?: (newData: DataT[]) => void | Promise<void>

  enableRowVirtualization?: boolean
  rowVirtualizerOverscan?: number
  enableStickyHeader?: boolean
  density?: 'xs' | 'md'
  highlightOnHover?: boolean
  striped?: boolean
  paperProps?: PaperProps & { className?: string }
  containerProps?: { className?: string; style?: CSSProperties }
}

type TableStateContextValue = { globalFilter: string | undefined }

const TableStateContext = createContext<TableStateContextValue>({ globalFilter: undefined })

export function Table<DataT extends RowData>(props: TableProps<DataT>) {
  const {
    data,
    columns,
    getRowId,
    entity,
    isLoading = false,
    EmptyStateProps,
    enableTopToolbar = true,
    enableTableHead = true,
    enableGlobalFilter = true,
    manualFiltering = false,
    pagination,
    actionsColumnPosition = 'last',
    actionsColumnSize = 140,
    rowActionsInclude,
    renderRowActions,
    enableRowOrdering = false,
    onRowOrderChange,
    enableRowVirtualization = false,
    enableStickyHeader = false,
    density = 'md',
    highlightOnHover = true,
  } = props

  useAutoScrollOnDrag(enableRowOrdering)

  // A drop moves rows by their place in `data`, which only matches what is shown while unsorted.
  const enableSorting = !!props.enableSorting && !enableRowOrdering

  const manualSorting = props.sorting !== undefined
  const [ownSorting, setOwnSorting] = useState<SortingState>(props.initialSorting ?? emptySorting)
  const sorting = props.sorting ?? ownSorting
  const [ownGlobalFilter, setOwnGlobalFilter] = useState('')
  const globalFilter = props.globalFilter ?? ownGlobalFilter
  const columnFilters = props.columnFilters ?? noColumnFilters
  const [showColumnFilters, setShowColumnFilters] = useState(false)

  // Search ranks results by match, so a sort is set aside while it runs and restored afterwards.
  const canRankResults = !manualFiltering && !manualSorting
  const lastSortingRef = useRef(sorting)

  const resetPageIndex = () => {
    if (pagination && pagination.state.pageIndex !== 0) pagination.onChange(old => ({ ...old, pageIndex: 0 }))
  }

  const setSorting = (updater: Updater<SortingState>) => {
    const next = functionalUpdate(updater, sorting)
    lastSortingRef.current = next
    if (!manualSorting) setOwnSorting(next)
    props.onSortingChange?.(next)
    resetPageIndex()
  }

  const setGlobalFilter = (value: string) => {
    if (props.globalFilter === undefined) setOwnGlobalFilter(value)
    props.onGlobalFilterChange?.(value)
    if (canRankResults && enableSorting) {
      if (value) {
        if (sorting.length) setOwnSorting(emptySorting)
      } else {
        setOwnSorting(lastSortingRef.current)
      }
    }
    resetPageIndex()
  }

  const setColumnFilters = (updater: Updater<ColumnFiltersState>) => {
    props.onColumnFiltersChange?.(functionalUpdate(updater, columnFilters))
    resetPageIndex()
  }

  const hasFilterableColumns =
    enableTopToolbar && !!props.onColumnFiltersChange && columns.some(column => column.meta?.filter)
  const showActionsColumn = !!(rowActionsInclude || renderRowActions)
  const allColumns = useMemo(() => {
    const actionsColumn: TableColumnDef<DataT> = {
      id: ACTIONS_COLUMN_ID,
      size: actionsColumnSize,
      enableGlobalFilter: false,
      enableSorting: false,
      enableHiding: false,
      meta: { grow: false },
    }
    const dragColumn: TableColumnDef<DataT> = {
      id: DRAG_COLUMN_ID,
      size: 60,
      enableGlobalFilter: false,
      enableSorting: false,
      enableHiding: false,
      meta: { grow: false },
    }
    return [
      ...(enableRowOrdering ? [dragColumn] : []),
      ...(showActionsColumn && actionsColumnPosition === 'first' ? [actionsColumn] : []),
      ...columns,
      ...(showActionsColumn && actionsColumnPosition === 'last' ? [actionsColumn] : []),
    ]
  }, [columns, enableRowOrdering, showActionsColumn, actionsColumnPosition, actionsColumnSize])

  const table = useTable({
    features: appTableFeatures,
    data,
    columns: allColumns,
    defaultColumn,
    getRowId,
    enableSorting,
    manualSorting,
    enableMultiSort: props.enableMultiSort,
    enableGlobalFilter,
    manualFiltering,
    globalFilterFn: 'fuzzy',
    manualPagination: !!pagination,
    rowCount: pagination?.rowCount,
    state: {
      sorting,
      globalFilter: globalFilter || undefined,
      columnFilters,
      columnVisibility: props.columnVisibility ?? noColumnVisibility,
      ...(pagination && { pagination: pagination.state }),
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: updater => setGlobalFilter(functionalUpdate(updater, globalFilter) ?? ''),
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: updater => {
      if (pagination) pagination.onChange(updater)
    },
  })

  const rowModelRows = table.getRowModel().rows
  const rows = useMemo(
    () =>
      canRankResults && globalFilter && !sorting.length
        ? rankByGlobalMatch(rowModelRows)
        : (rowModelRows as TableRow<DataT>[]),
    [canRankResults, globalFilter, sorting, rowModelRows],
  )

  // A shrinking total can strand the pager past its last page.
  const rowCount = pagination?.rowCount
  const clampPageToRowCount = useEffectEvent((rowCount: number | undefined) => {
    if (!pagination || isLoading || rowCount === undefined) return
    const { pageIndex, pageSize } = pagination.state
    if (pageIndex * pageSize >= rowCount && pageIndex > 0) {
      pagination.onChange(old => ({ ...old, pageIndex: Math.max(0, Math.ceil(rowCount / pageSize) - 1) }))
    }
  })
  useEffect(() => {
    clampPageToRowCount(rowCount)
  }, [rowCount])

  const [draggingRow, setDraggingRow] = useState<TableRow<DataT> | null>(null)
  const [hoveredRow, setHoveredRow] = useState<TableRow<DataT> | null>(null)
  const dragRef = useRef<{ dragging: TableRow<DataT> | null; droppedOn: TableRow<DataT> | null }>({
    dragging: null,
    droppedOn: null,
  })

  const rowOrdering: RowOrdering<DataT> | undefined = enableRowOrdering
    ? {
        draggingRow,
        hoveredRow,
        onDragStart: row => {
          dragRef.current = { dragging: row, droppedOn: null }
          setDraggingRow(row)
        },
        onDragEnter: row => {
          if (dragRef.current.dragging) setHoveredRow(row)
        },
        // A drag cancelled with Escape or released outside the rows ends without a drop.
        onDrop: row => {
          if (dragRef.current.dragging) dragRef.current.droppedOn = row
        },
        onDragEnd: () => {
          const { dragging, droppedOn } = dragRef.current
          // Positions are read now, by id, since a refresh during the drag can move rows.
          const rowsById = table.getCoreRowModel().rowsById
          const from = dragging && rowsById[dragging.id]?.index
          const to = droppedOn && rowsById[droppedOn.id]?.index
          if (from != null && to != null && onRowOrderChange && from !== to) {
            const newData = [...data]
            const [removed] = newData.splice(from, 1)
            newData.splice(to, 0, removed)
            onRowOrderChange(newData)
          }
          dragRef.current = { dragging: null, droppedOn: null }
          setDraggingRow(null)
          setHoveredRow(null)
        },
      }
    : undefined

  const renderSpecialCell = (cell: Cell<AppTableFeatures, DataT, unknown>) => {
    if (cell.column.id === DRAG_COLUMN_ID && rowOrdering) {
      return <TableRowGrabHandle row={cell.row} rowOrdering={rowOrdering} />
    }
    if (cell.column.id === ACTIONS_COLUMN_ID) {
      if (renderRowActions) return renderRowActions({ row: cell.row })
      return (
        <TableRowActions
          row={cell.row}
          include={rowActionsInclude}
          tooltipLabels={props.rowActionsTooltipLabels}
          onActionClick={props.onRowActionClick}
          actionButtonProps={props.rowActionsButtonProps}
        />
      )
    }
    return undefined
  }

  // State rather than a ref: the virtualizer reads it in a layout effect, which runs before this
  // ancestor's ref is attached, so it needs the render that setting the element triggers.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const visibleColumns = table.getVisibleLeafColumns()

  const bodyProps: TableBodyProps<DataT> = {
    rows,
    visibleColumns,
    isLoading,
    density,
    rowOrdering,
    renderSpecialCell,
    emptyFallback: (
      <TableEmptyRowsFallback
        entity={entity}
        globalFilter={globalFilter}
        hasColumnFilters={columnFilters.length > 0}
        EmptyStateProps={EmptyStateProps}
      />
    ),
    skeletonRowCount: Math.min(pagination?.state.pageSize ?? 10, 20),
  }

  return (
    <TableStateContext.Provider value={{ globalFilter: globalFilter || undefined }}>
      <Paper
        shadow="none"
        withBorder={false}
        {...props.paperProps}
        className={theme.cx(classes.tablePaper, props.paperProps?.className ?? classes.paper)}>
        {enableTopToolbar && (
          <TableTopToolbar
            entity={entity}
            include={props.toolbarActionsInclude}
            onActionClick={props.onToolbarActionClick}
            toolbarActionsLabelMap={props.toolbarActionsLabelMap}
            toolbarActionsDisabled={props.toolbarActionsDisabled}
            toolbarActionsDisabledTooltip={props.toolbarActionsDisabledTooltip}
            extraToolbarActions={props.extraToolbarActions}
            searchLabel={props.searchLabel}
            search={
              enableGlobalFilter
                ? { value: globalFilter, onChange: setGlobalFilter, debounce: manualFiltering ? 500 : 250 }
                : undefined
            }
            filterToggle={
              hasFilterableColumns
                ? { shown: showColumnFilters, onToggle: () => setShowColumnFilters(shown => !shown) }
                : undefined
            }
          />
        )}
        <Box
          ref={setScrollElement}
          {...props.containerProps}
          className={theme.cx(classes.container, props.containerProps?.className)}>
          <LoadingOverlay visible={isLoading} zIndex={2} />
          <MantineTable
            highlightOnHover={highlightOnHover}
            striped={props.striped}
            horizontalSpacing={density}
            verticalSpacing={density}
            className={classes.table}>
            {enableTableHead && (
              <MantineTable.Thead
                pos={enableStickyHeader ? 'sticky' : 'relative'}
                className={theme.cx(classes.thead, enableStickyHeader && classes.theadSticky)}>
                {table.getHeaderGroups().map(headerGroup => (
                  <MantineTable.Tr
                    key={headerGroup.id}
                    className={theme.cx(classes.headRow, enableStickyHeader && classes.headRowSticky)}>
                    {headerGroup.headers.map(header => (
                      <TableHeadCell
                        key={header.id}
                        header={header}
                        sorting={sorting}
                        showFilter={showColumnFilters}
                        onShowFilters={hasFilterableColumns ? () => setShowColumnFilters(true) : undefined}
                      />
                    ))}
                  </MantineTable.Tr>
                ))}
              </MantineTable.Thead>
            )}
            {enableRowVirtualization ? (
              <TableVirtualizedBody
                {...bodyProps}
                scrollElement={scrollElement}
                overscan={props.rowVirtualizerOverscan}
              />
            ) : (
              <TableBody {...bodyProps} />
            )}
          </MantineTable>
        </Box>
        {pagination && <TableBottomToolbar pagination={pagination} />}
      </Paper>
    </TableStateContext.Provider>
  )
}

type RowOrdering<DataT extends RowData> = {
  draggingRow: TableRow<DataT> | null
  hoveredRow: TableRow<DataT> | null
  onDragStart: (row: TableRow<DataT>) => void
  onDragEnter: (row: TableRow<DataT>) => void
  onDrop: (row: TableRow<DataT>) => void
  onDragEnd: () => void
}

type TableBodyProps<DataT extends RowData> = {
  rows: TableRow<DataT>[]
  visibleColumns: Column<AppTableFeatures, DataT, unknown>[]
  isLoading: boolean
  density: 'xs' | 'md'
  rowOrdering?: RowOrdering<DataT>
  renderSpecialCell: (cell: Cell<AppTableFeatures, DataT, unknown>) => ReactNode | undefined
  emptyFallback: ReactNode
  skeletonRowCount: number
}

function TableBody<DataT extends RowData>(props: TableBodyProps<DataT>) {
  const { rows, isLoading, visibleColumns } = props
  const showSkeletonRows = isLoading && !rows.length

  return (
    <MantineTable.Tbody className={theme.cx(classes.tbody, !rows.length && !showSkeletonRows && classes.tbodyEmpty)}>
      {showSkeletonRows ? (
        Array.from({ length: props.skeletonRowCount }, (_, index) => (
          <MantineTable.Tr key={index} className={classes.row}>
            {visibleColumns.map(column => (
              <TableBodyCellFrame key={column.id} column={column} density={props.density}>
                <TableCellSkeleton column={column} />
              </TableBodyCellFrame>
            ))}
          </MantineTable.Tr>
        ))
      ) : !rows.length ? (
        <MantineTable.Tr className={theme.cx(classes.row, classes.emptyRow)}>
          <td className={classes.emptyCell} colSpan={visibleColumns.length}>
            {props.emptyFallback}
          </td>
        </MantineTable.Tr>
      ) : (
        rows.map(row => <TableBodyRow key={row.id} row={row} {...props} />)
      )}
    </MantineTable.Tbody>
  )
}

const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.includes('Firefox')

function TableVirtualizedBody<DataT extends RowData>(
  props: TableBodyProps<DataT> & { scrollElement: HTMLDivElement | null; overscan?: number },
) {
  const { rows, rowOrdering } = props
  // By id: a data refresh mid-drag replaces the row objects, and the dragged row must stay mounted.
  const draggingId = rowOrdering?.draggingRow?.id
  const draggingIndex = draggingId === undefined ? -1 : rows.findIndex(row => row.id === draggingId)

  // eslint-disable-next-line react-hooks/incompatible-library -- React Compiler is not enabled, and this body re-renders on scroll by design
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => (props.density === 'xs' ? 42.7 : 54.7),
    getScrollElement: () => props.scrollElement,
    measureElement: isFirefox ? undefined : element => element.getBoundingClientRect().height,
    overscan: props.overscan ?? 4,
    // Keeps the row being dragged mounted while it is scrolled out of view.
    rangeExtractor: useCallback(
      (range: Parameters<typeof defaultRangeExtractor>[0]) => {
        const indexes = defaultRangeExtractor(range)
        if (draggingIndex >= 0 && !indexes.includes(draggingIndex)) {
          indexes.push(draggingIndex)
          indexes.sort((a, b) => a - b)
        }
        return indexes
      },
      [draggingIndex],
    ),
  })

  if (!rows.length) return <TableBody {...props} />

  return (
    <MantineTable.Tbody className={classes.tbody} style={{ height: rowVirtualizer.getTotalSize() }}>
      {rowVirtualizer.getVirtualItems().map(virtualRow => {
        const row = rows[virtualRow.index]
        return (
          <TableBodyRow key={row.id} row={row} virtualRow={virtualRow} rowVirtualizer={rowVirtualizer} {...props} />
        )
      })}
    </MantineTable.Tbody>
  )
}

function TableBodyRow<DataT extends RowData>(
  props: TableBodyProps<DataT> & {
    row: TableRow<DataT>
    virtualRow?: VirtualItem
    rowVirtualizer?: Virtualizer<HTMLDivElement, Element>
  },
) {
  const { row, virtualRow, rowVirtualizer, rowOrdering, isLoading } = props
  // Stable, so a row is measured when it mounts and then by the virtualizer's resize observer,
  // rather than re-measured on every render.
  const setRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (node) rowVirtualizer?.measureElement(node)
    },
    [rowVirtualizer],
  )

  return (
    <MantineTable.Tr
      data-index={virtualRow?.index}
      data-dragging-row={rowOrdering?.draggingRow?.id === row.id || undefined}
      data-hovered-row-target={rowOrdering?.hoveredRow?.id === row.id || undefined}
      ref={setRowRef}
      className={theme.cx(classes.row, virtualRow && classes.rowVirtualized)}
      style={virtualRow ? { transform: `translateY(${virtualRow.start}px)` } : undefined}
      {...(rowOrdering && {
        onDragEnter: () => rowOrdering.onDragEnter(row),
        onDragOver: (e: DragEvent) => e.preventDefault(),
        onDrop: (e: DragEvent) => {
          e.preventDefault()
          rowOrdering.onDrop(row)
        },
      })}>
      {row.getVisibleCells().map(cell => (
        <TableBodyCellFrame key={cell.id} column={cell.column} density={props.density} virtualized={!!virtualRow}>
          {isLoading ? <TableCellSkeleton column={cell.column} /> : (props.renderSpecialCell(cell) ?? renderCell(cell))}
        </TableBodyCellFrame>
      ))}
    </MantineTable.Tr>
  )
}

// Called rather than mounted: a column list rebuilt on every render then keeps the elements its
// cells return, where mounting each `cell` function as a component would remount them and drop an
// input's focus mid-edit.
const renderCell = <DataT extends RowData>(cell: Cell<AppTableFeatures, DataT, unknown>): ReactNode => {
  const template = cell.column.columnDef.cell
  return typeof template === 'function' ? template(cell.getContext()) : template
}

const renderHeader = <DataT extends RowData>(header: Header<AppTableFeatures, DataT, unknown>): ReactNode => {
  const template = header.column.columnDef.header
  return typeof template === 'function' ? template(header.getContext()) : template
}

const plainHeaderText = (header: unknown) => (typeof header === 'string' ? header : undefined)

const isDisplayColumn = <DataT extends RowData>(column: Column<AppTableFeatures, DataT, unknown>) =>
  column.id === ACTIONS_COLUMN_ID || column.id === DRAG_COLUMN_ID

// Each column takes its size and grows into spare width in proportion to it, unless `grow` is false.
const columnWidthStyle = <DataT extends RowData>(
  column: Column<AppTableFeatures, DataT, unknown>,
  size: number,
): CSSProperties => {
  const minSize = column.columnDef.minSize ?? 30
  return {
    minWidth: `max(${size}px, ${minSize}px)`,
    width: `${size}px`,
    flex: column.columnDef.meta?.grow === false ? '0 0 auto' : `${size} 0 auto`,
  }
}

function TableBodyCellFrame<DataT extends RowData>(props: {
  column: Column<AppTableFeatures, DataT, unknown>
  density: 'xs' | 'md'
  virtualized?: boolean
  children: ReactNode
}) {
  const { column } = props
  const align = column.columnDef.meta?.align ?? 'left'

  return (
    <MantineTable.Td
      className={theme.cx(
        classes.cell,
        props.virtualized && classes.cellVirtualized,
        !isDisplayColumn(column) && classes.cellData,
        props.density === 'xs' && classes.cellNowrap,
      )}
      style={{ ...columnWidthStyle(column, column.getSize()), ['--table-cell-align' as string]: align }}>
      {props.children}
    </MantineTable.Td>
  )
}

function TableCellSkeleton<DataT extends RowData>(props: { column: Column<AppTableFeatures, DataT, unknown> }) {
  const size = props.column.getSize()
  // Fixed rather than random, so screenshots stay comparable between runs.
  return <Skeleton height={20} width={Math.round(isDisplayColumn(props.column) ? size / 2 : (size * 2) / 3)} />
}

const sortDirectionToAria = { asc: 'ascending', desc: 'descending' } as const

function TableHeadCell<DataT extends RowData>(props: {
  header: Header<AppTableFeatures, DataT, unknown>
  sorting: SortingState
  showFilter: boolean
  onShowFilters?: () => void
}) {
  const { header } = props
  const { column } = header
  const { columnDef } = column
  const canSort = column.getCanSort()
  const sorted = column.getIsSorted()
  const headerText = plainHeaderText(columnDef.header)
  const label = columnDef.meta?.label ?? headerText ?? ''
  const isDataColumn = !isDisplayColumn(column)
  const filter = props.showFilter && !header.isPlaceholder ? columnDef.meta?.filter : undefined
  const onShowFilters =
    !props.showFilter && columnDef.meta?.filter && column.getIsFiltered() ? props.onShowFilters : undefined

  const sortLabel = sorted
    ? `Sorted by ${label} ${sorted === 'desc' ? 'descending' : 'ascending'}`
    : `Sort by ${label} ${column.getNextSortingOrder() === 'desc' ? 'descending' : 'ascending'}`
  const sortIndex = column.getSortIndex()

  const sortButton = (
    <ActionIcon aria-label={sortLabel} data-sorted={sorted || undefined} className={classes.sortIcon}>
      {sorted === 'desc' ? (
        <IconSortDescending size="100%" />
      ) : sorted === 'asc' ? (
        <IconSortAscending size="100%" />
      ) : (
        <IconArrowsSort size="100%" />
      )}
    </ActionIcon>
  )

  return (
    <MantineTable.Th
      colSpan={header.colSpan}
      align="left"
      aria-sort={canSort ? (sorted ? sortDirectionToAria[sorted] : 'none') : undefined}
      className={theme.cx(classes.headCell, canSort && classes.headCellNoSelect)}
      style={columnWidthStyle(column, header.getSize())}>
      {!header.isPlaceholder && (
        <Flex className={theme.cx(classes.headCellContent, props.showFilter && classes.headCellContentWithFilter)}>
          <Flex
            className={theme.cx(
              classes.headCellLabels,
              canSort && classes.headCellLabelsSortable,
              isDataColumn && classes.headCellLabelsData,
            )}
            onClick={column.getToggleSortingHandler()}>
            <Flex
              className={theme.cx(
                classes.headCellText,
                isDataColumn && classes.headCellTextClipped,
                (headerText?.length ?? 0) < 20 && classes.headCellTextNowrap,
              )}>
              {renderHeader(header)}
            </Flex>
            {canSort && (
              <Tooltip label={sortLabel} openDelay={1000} withinPortal>
                {props.sorting.length < 2 || sortIndex === -1 ? (
                  sortButton
                ) : (
                  <Indicator className={classes.multiSortIndicator} inline label={sortIndex + 1} offset={4}>
                    {sortButton}
                  </Indicator>
                )}
              </Tooltip>
            )}
            {onShowFilters && (
              <Tooltip label={`Filtered by ${label}`} withinPortal>
                <ActionIcon
                  aria-label={`Filtered by ${label}. Show filters`}
                  className={classes.filteredIcon}
                  onClick={event => {
                    event.stopPropagation()
                    onShowFilters()
                  }}>
                  <IconFilter size="100%" />
                </ActionIcon>
              </Tooltip>
            )}
          </Flex>
          <Flex className={classes.headCellActions} />
        </Flex>
      )}
      {filter && (
        <Box className={classes.headCellFilter}>
          {filter({ value: column.getFilterValue(), onChange: value => column.setFilterValue(value) })}
        </Box>
      )}
    </MantineTable.Th>
  )
}

/** Renders a cell's value with the current search term highlighted, as plain-text cells show it. */
export function TableCellValue(props: { cell: Cell<AppTableFeatures, any, unknown> }) {
  const { globalFilter } = useContext(TableStateContext)
  const value = props.cell.renderValue()

  if (
    value != null &&
    value !== '' &&
    (typeof value === 'string' || typeof value === 'number') &&
    globalFilter &&
    props.cell.column.getCanGlobalFilter()
  ) {
    return (
      <Highlight component="span" color="yellow.3" highlight={globalFilter.split(' ')}>
        {value.toString()}
      </Highlight>
    )
  }

  return (value as ReactNode) ?? null
}

function TableRowGrabHandle<DataT extends RowData>(props: { row: TableRow<DataT>; rowOrdering: RowOrdering<DataT> }) {
  return (
    <Tooltip label="Move" openDelay={1000} withinPortal>
      <ActionIcon
        aria-label="Move"
        draggable
        className={classes.grabHandle}
        color="gray"
        size="sm"
        variant="transparent"
        onClick={e => e.stopPropagation()}
        onDragStart={event => {
          const rowElement = event.currentTarget.closest('tr')
          if (rowElement) event.dataTransfer.setDragImage(rowElement, 0, 0)
          props.rowOrdering.onDragStart(props.row)
        }}
        onDragEnd={props.rowOrdering.onDragEnd}>
        <IconGripHorizontal size="100%" />
      </ActionIcon>
    </Tooltip>
  )
}

function TableBottomToolbar(props: { pagination: TablePagination }) {
  const isMobile = useMediaQuery('(max-width: 720px)')

  return (
    <Box className={classes.bottomToolbar}>
      <Box className={classes.bottomToolbarContent}>
        <span />
        <Box className={theme.cx(classes.paginator, isMobile && classes.paginatorStacked)}>
          <TablePager pagination={props.pagination} />
        </Box>
      </Box>
    </Box>
  )
}

const rowsPerPageOptions = [5, 10, 15, 20, 25, 30, 50, 100].map(String)

function TablePager(props: { pagination: TablePagination }) {
  const { state, rowCount, onChange, showRowsPerPage = true } = props.pagination
  const { pageIndex, pageSize } = state
  const numberOfPages = Math.ceil(rowCount / pageSize)
  const withEdges = numberOfPages > 2
  const firstRowIndex = pageIndex * pageSize
  const lastRowIndex = Math.min(firstRowIndex + pageSize, rowCount)
  const setPageIndex = (index: number) => onChange(old => ({ ...old, pageIndex: index }))
  const rowsPerPageLabelId = useId()

  return (
    <Box className={classes.pagination}>
      {showRowsPerPage && (
        <Group gap="xs">
          <Text id={rowsPerPageLabelId}>Rows per page</Text>
          <Select
            allowDeselect={false}
            aria-labelledby={rowsPerPageLabelId}
            className={classes.pageSize}
            data={rowsPerPageOptions}
            onChange={value => onChange({ pageIndex: 0, pageSize: Number(value) })}
            value={String(pageSize)}
          />
        </Group>
      )}
      <Text>{`${lastRowIndex === 0 ? 0 : (firstRowIndex + 1).toLocaleString()}-${lastRowIndex.toLocaleString()} of ${rowCount.toLocaleString()}`}</Text>
      <Group gap={6}>
        {withEdges && (
          <ActionIcon
            aria-label="Go to first page"
            color="gray"
            disabled={pageIndex <= 0}
            onClick={() => setPageIndex(0)}
            variant="subtle">
            <IconChevronLeftPipe />
          </ActionIcon>
        )}
        <ActionIcon
          aria-label="Go to previous page"
          color="gray"
          disabled={pageIndex <= 0}
          onClick={() => setPageIndex(pageIndex - 1)}
          variant="subtle">
          <IconChevronLeft />
        </ActionIcon>
        <ActionIcon
          aria-label="Go to next page"
          color="gray"
          disabled={lastRowIndex >= rowCount}
          onClick={() => setPageIndex(pageIndex + 1)}
          variant="subtle">
          <IconChevronRight />
        </ActionIcon>
        {withEdges && (
          <ActionIcon
            aria-label="Go to last page"
            color="gray"
            disabled={lastRowIndex >= rowCount}
            onClick={() => setPageIndex(numberOfPages - 1)}
            variant="subtle">
            <IconChevronRightPipe />
          </ActionIcon>
        )}
      </Group>
    </Box>
  )
}

type TableRowActionsProps<DataT extends RowData> = {
  row: TableRow<DataT>
  include?: TypeOrTypeFn<Array<RowAction> | undefined, [TableRow<DataT>]>
  actionButtonProps?: TypeOrTypeFn<Partial<Record<RowAction, ActionIconProps>> | undefined, [TableRow<DataT>]>
  onActionClick?: (action: RowAction, row: TableRow<DataT>) => void
  tooltipLabels?: TypeOrTypeFn<Partial<Record<RowAction, string | undefined>> | undefined, [TableRow<DataT>]>
}

export function TableRowActions<DataT extends RowData>(props: TableRowActionsProps<DataT>) {
  const { onActionClick, row } = props

  const include = (typeof props.include === 'function' ? props.include(row) : props.include) ?? []
  const actionButtonProps =
    typeof props.actionButtonProps === 'function' ? props.actionButtonProps(row) : props.actionButtonProps
  const tooltipLabels = typeof props.tooltipLabels === 'function' ? props.tooltipLabels(row) : props.tooltipLabels

  if (!include.length) return null

  return (
    <Group gap="xs" justify="flex-end" flex="1 1 100%" wrap="nowrap">
      {include.map(a => {
        const tooltipLabel = tooltipLabels?.[a]

        return (
          <TableRowActionButton
            key={a}
            action={a}
            tooltipLabel={tooltipLabel}
            onClick={() => onActionClick?.(a, row)}
            actionButtonProps={actionButtonProps?.[a]}
          />
        )
      })}
    </Group>
  )
}

// Fallback labels keep icon-only row actions named for screen readers
// even when no tooltipLabel is provided. Keys match `rowActionsIconRegistry`.
const rowActionsAriaLabelMap: Record<RowAction, string> = {
  edit: 'Edit',
  view: 'View',
  delete: 'Delete',
  archive: 'Archive',
  demo: 'Demo',
  menu: 'More actions',
  changeStatus: 'Change status',
  owners: 'Manage owners',
  lock: 'Locked',
}

// Extra button props let it sit directly in Menu.Target, which puts aria-expanded
// and the toggle handler on the element it's given.
export function TableRowActionButton({
  action,
  actionButtonProps,
  tooltipLabel,
  className,
  ...buttonProps
}: ComponentPropsWithRef<'button'> & {
  action: RowAction
  actionButtonProps?: ActionIconProps
  tooltipLabel?: string
}) {
  const Icon = rowActionsIconRegistry[action]
  const color = rowActionsIconColorMap[action]
  const stroke = rowActionsIconStrokeMap[action]
  const ariaLabel = tooltipLabel ?? rowActionsAriaLabelMap[action]

  return (
    <Tooltip
      withArrow
      arrowSize={12}
      position="top"
      offset={8}
      color={actionButtonProps?.disabled || buttonProps.disabled ? 'gray' : color}
      label={tooltipLabel}
      disabled={!tooltipLabel}>
      <ActionIcon
        aria-label={ariaLabel}
        variant="subtle"
        color={color}
        {...actionButtonProps}
        {...buttonProps}
        className={theme.cx(classes.actionButton, actionButtonProps?.className, className)}>
        <Icon className={classes.rowActionIcon} stroke={stroke} aria-hidden="true" />
      </ActionIcon>
    </Tooltip>
  )
}

type TableTopToolbarProps = {
  entity: { singular: string; plural: string } | undefined
  include?: TableToolbarAction[]
  onActionClick?: (action: TableToolbarAction) => void
  toolbarActionsLabelMap?: Partial<Record<TableToolbarAction, string>>
  toolbarActionsDisabled?: Partial<Record<TableToolbarAction, boolean>>
  toolbarActionsDisabledTooltip?: Partial<Record<TableToolbarAction, string>>
  extraToolbarActions?: ReactNode
  searchLabel?: string
  search?: { value: string; onChange: (value: string) => void; debounce: number }
  filterToggle?: { shown: boolean; onToggle: () => void }
}

export function TableTopToolbar(props: TableTopToolbarProps) {
  const {
    entity,
    include = [],
    onActionClick,
    toolbarActionsLabelMap,
    toolbarActionsDisabled = {},
    toolbarActionsDisabledTooltip = {},
    extraToolbarActions,
    searchLabel,
    search,
    filterToggle,
  } = props

  const searchAccessibleLabel = searchLabel ?? `Search ${entity ? entity.plural : ''}`

  return (
    <Group justify="space-between" mb={theme.spacing.xl}>
      {search ? <TableSearchInput label={searchAccessibleLabel} {...search} /> : <Box />}

      {include.length || extraToolbarActions || filterToggle ? (
        <Group>
          {extraToolbarActions}
          {filterToggle && (
            <Tooltip label="Show/Hide filters" withinPortal>
              <ActionIcon
                aria-label="Show/Hide filters"
                aria-pressed={filterToggle.shown}
                color="gray"
                onClick={filterToggle.onToggle}
                size="lg"
                variant="subtle">
                {filterToggle.shown ? <IconFilterOff /> : <IconFilter />}
              </ActionIcon>
            </Tooltip>
          )}
          {include.map(a => {
            const isDisabled = toolbarActionsDisabled[a] ?? false
            const tooltipLabel = toolbarActionsDisabledTooltip[a]
            const button = (
              <Button
                key={a}
                variant={a === 'new' ? 'filled' : 'light'}
                disabled={isDisabled}
                onClick={() => onActionClick?.(a)}>
                {a === 'new'
                  ? `${toolbarActionsLabelMap?.new ?? defaultToolbarActionsLabelMap.new} ${entity ? entity.singular : ''}`
                  : (toolbarActionsLabelMap?.[a] ?? defaultToolbarActionsLabelMap[a])}
              </Button>
            )

            if (isDisabled && tooltipLabel) {
              return (
                <Tooltip key={a} label={tooltipLabel} withArrow>
                  <span>{button}</span>
                </Tooltip>
              )
            }

            return button
          })}
        </Group>
      ) : (
        <Box />
      )}
    </Group>
  )
}

function TableSearchInput(props: {
  label: string
  value: string
  onChange: (value: string) => void
  debounce: number
}) {
  const [searchValue, setSearchValue] = useState(props.value)
  const [shownValue, setShownValue] = useState(props.value)
  // Follows a term set from outside the box, as well as the one typed into it.
  if (props.value !== shownValue) {
    setShownValue(props.value)
    setSearchValue(props.value)
  }
  const commit = useDebouncedCallback(props.onChange, props.debounce)

  const clear = () => {
    commit.cancel()
    setSearchValue('')
    props.onChange('')
  }

  return (
    <Box className={classes.globalFilterGroup}>
      <TextInput
        size="sm"
        variant="filled"
        mt={0}
        aria-label={props.label}
        placeholder={props.label}
        classNames={{ root: classes.globalFilter, input: classes.globalFilterInput }}
        leftSection={<IconSearch stroke={1.5} />}
        rightSection={
          searchValue ? (
            <ActionIcon aria-label="Clear search" color="gray" onClick={clear} size="sm" variant="transparent">
              <Tooltip label="Clear search" withinPortal>
                <IconX />
              </Tooltip>
            </ActionIcon>
          ) : null
        }
        value={searchValue}
        onChange={event => {
          setSearchValue(event.currentTarget.value)
          commit(event.currentTarget.value)
        }}
      />
    </Box>
  )
}

export type TableToolbarAction = 'new' | 'uploadMultiple'

const defaultToolbarActionsLabelMap: Record<TableToolbarAction, string> = {
  new: 'New',
  uploadMultiple: 'Upload Multiple',
}

type TableEmptyRowsFallbackProps = {
  entity: TableProps<RowData>['entity']
  globalFilter: string
  hasColumnFilters: boolean
  EmptyStateProps?: MarkOptional<EmptyStateProps, 'title'>
}

function TableEmptyRowsFallback(props: TableEmptyRowsFallbackProps) {
  const { entity, globalFilter, hasColumnFilters, EmptyStateProps } = props

  return (
    <EmptyState
      size="sm"
      Icon={globalFilter || hasColumnFilters ? IconZoomCancel : (entity?.Icon ?? IconCircleX)}
      title={`No ${entity ? entity.plural : 'Records'} to Display`}
      description={
        globalFilter
          ? 'Reset the filter or try a different search term.'
          : hasColumnFilters
            ? 'Clear or change a filter to see more.'
            : undefined
      }
      {...EmptyStateProps}
    />
  )
}

export const HeaderCellWithTooltip = <DataT extends RowData, TValue>(props: {
  column: Column<AppTableFeatures, DataT, TValue>
  tooltip: string
  header?: ReactNode
}) => {
  return (
    <Tooltip withArrow arrowSize={12} position="top" offset={8} label={props.tooltip}>
      <Group gap={theme.rem(4)} justify="flex-start" align="center">
        {props.header ?? props.column.columnDef.meta?.label ?? plainHeaderText(props.column.columnDef.header)}
        <IconHelp stroke={1.5} size={20} />
      </Group>
    </Tooltip>
  )
}
